/**
 * LiveMap — Unified Light-Theme Map Component
 *
 * Single map for all contexts (normal + fullscreen).
 * Uses browser-native requestFullscreen() for immersive view.
 * Light theme only (Silver tiles). No dark mode.
 * Auto-fit bounds, pulsing performance rings, competitor pins, AI Scout.
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import type { User } from '../../../domain/entities';
import { hasTabVariant } from '../../../core/security';
import { apiFetch } from '../../../data/ApiModuleRepository';
import { cn } from '../../../ui/utils';
import { useToast } from '../../../ui/Toast';
import {
  MapPin, RefreshCw, Layers, X, Loader2,
  Building2, Flag, Radar, AlertTriangle, Maximize, Minimize,
  ToggleLeft, ToggleRight, Trophy, Activity, TrendingUp, Zap, Pause, Play, Monitor,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  useMapData,
  type MapLocation, type MapSettings, type CompetitorTag, type ScoutedCompetitor, type MapLiveFeedEvent,
  DEFAULT_SETTINGS, SILVER_TILE, TILE_ATTR,
  getEffectiveTarget, getSalesRatio, getPerformanceColor, getPulseSpeed,
  formatCurrency, geoDistanceMeters, getMediaUrl,
} from '../hooks/useMapData';

// ═══════════════════════════════════════════════════════════
// WAR ROOM TYPES
// ═══════════════════════════════════════════════════════════

type WarRoomEvent = MapLiveFeedEvent;

function formatTimeAgo(ts: number): string {
  const diff = Math.floor((Date.now() - ts) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}

// ═══════════════════════════════════════════════════════════
// LIVE MAP — Unified Component
// ═══════════════════════════════════════════════════════════

export function LiveMap({ user }: { user: User | null }) {
  const isAdmin = hasTabVariant(user, 'market-map', 'live-map', 'admin');
  const toast = useToast();
  const {
    locations, settings, competitorTags, loading, error,
    liveFeed, internal, competitors, loadData,
    totalSales, totalTarget, overallRatio,
    greenCount, amberCount, redCount, champions,
  } = useMapData();

  // Fullscreen state
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Layer prefs (persisted to localStorage)
  const savedLayerPrefs = useRef(() => {
    try { return JSON.parse(localStorage.getItem('map-layer-prefs') || 'null'); } catch { return null; }
  });
  const lp = savedLayerPrefs.current();
  const [showInternal, setShowInternal] = useState(lp?.showInternal ?? true);
  const [showCompetitors, setShowCompetitors] = useState(lp?.showCompetitors ?? true);
  const [showPerformance, setShowPerformance] = useState(lp?.showPerformance ?? true);
  const [showLabels, setShowLabels] = useState(lp?.showLabels ?? false);
  const [showCoverage, setShowCoverage] = useState(lp?.showCoverage ?? true);
  const [selectedLoc, setSelectedLoc] = useState<MapLocation | null>(null);
  const mapInstanceRef = useRef<any>(null);

  // Competitor tag filtering
  const [hiddenTagIds, setHiddenTagIds] = useState<Set<string>>(new Set());

  // AI Market Scout state
  const [scoutedResults, setScoutedResults] = useState<ScoutedCompetitor[]>([]);
  const [scouting, setScouting] = useState(false);
  const [scoutTarget, setScoutTarget] = useState<MapLocation | null>(null);
  const [importingPlaceId, setImportingPlaceId] = useState<string | null>(null);
  const [hoveredScouted, setHoveredScouted] = useState<{ comp: ScoutedCompetitor; x: number; y: number } | null>(null);

  // ── Map readiness (lazy load) ─────────────────────────
  const [mapReady, setMapReady] = useState(false);

  // ── War Room state (fullscreen only) ──────────────────
  const warRoomEvents = useMemo(() => liveFeed.slice(0, 20), [liveFeed]);
  const [spotlightLoc, setSpotlightLoc] = useState<{ loc: MapLocation; delta: number } | null>(null);
  const [intelligencePaused, setIntelligencePaused] = useState(false);
  const spotlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoCycleRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const spotlightActiveRef = useRef(false);
  const warRoomEventsRef = useRef(warRoomEvents);
  const lastSpotlightEventIdRef = useRef<string | null>(null);

  // Apply server defaults when settings arrive (if no saved prefs)
  useEffect(() => {
    if (!lp && settings.org_id) {
      setShowInternal(!!settings.show_internal);
      setShowCompetitors(!!settings.show_competitors);
      setShowPerformance(!!settings.show_performance);
    }
  }, [settings.org_id]);

  // Persist layer toggles
  useEffect(() => {
    localStorage.setItem('map-layer-prefs', JSON.stringify({
      showInternal, showCompetitors, showPerformance, showLabels, showCoverage,
    }));
  }, [showInternal, showCompetitors, showPerformance, showLabels, showCoverage]);

  // Fullscreen API — listen for changes (Esc key auto-handled by browser)
  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      // Invalidate map size after fullscreen transition — preserve current center + zoom
      setTimeout(() => {
        if (mapInstanceRef.current) {
          const center = mapInstanceRef.current.getCenter();
          const zoom = mapInstanceRef.current.getZoom();
          mapInstanceRef.current.invalidateSize({ animate: false });
          mapInstanceRef.current.setView(center, zoom, { animate: false });
        }
      }, 200);
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!containerRef.current) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      containerRef.current.requestFullscreen();
    }
  }, []);

  const visibleLocations = useMemo(() => {
    return locations.filter(l => {
      if (l.type === 'INTERNAL' && !showInternal) return false;
      if (l.type === 'COMPETITOR' && !showCompetitors) return false;
      if (l.type === 'COMPETITOR' && l.competitor_tag_id && hiddenTagIds.has(l.competitor_tag_id)) return false;
      return true;
    });
  }, [locations, showInternal, showCompetitors, hiddenTagIds]);

  // ── Brand-wise KPI pills ────────────────────────────────
  const brandKpis = useMemo(() => {
    const map = new Map<string, { name: string; sales: number; target: number }>();
    internal.forEach(loc => {
      const bName = loc.brand_name || 'Unknown';
      const bId = loc.brand_id || '__none__';
      if (!map.has(bId)) map.set(bId, { name: bName, sales: 0, target: 0 });
      const b = map.get(bId)!;
      b.sales += (loc.current_sales ?? 0);
      b.target += getEffectiveTarget(loc);
    });
    return [...map.values()].map(b => ({ ...b, ratio: b.target > 0 ? b.sales / b.target : 0 }));
  }, [internal]);

  // Portrait mode detection
  const isPortrait = isFullscreen && settings.tv_orientation === 'portrait';

  // Invalidate map size when portrait mode changes — preserve zoom
  useEffect(() => {
    if (mapInstanceRef.current) {
      setTimeout(() => {
        if (mapInstanceRef.current) {
          const center = mapInstanceRef.current.getCenter();
          const zoom = mapInstanceRef.current.getZoom();
          mapInstanceRef.current.invalidateSize({ animate: false });
          mapInstanceRef.current.setView(center, zoom, { animate: false });
        }
      }, 300);
    }
  }, [isPortrait]);

  // ── War Room: Keep refs in sync ─────────────────────────
  useEffect(() => { spotlightActiveRef.current = !!spotlightLoc; }, [spotlightLoc]);
  useEffect(() => { warRoomEventsRef.current = warRoomEvents; }, [warRoomEvents]);

  // ── War Room: Clear state on fullscreen exit ────────────
  useEffect(() => {
    if (!isFullscreen) {
      setSpotlightLoc(null);
      setIntelligencePaused(false);
      if (spotlightTimeoutRef.current) clearTimeout(spotlightTimeoutRef.current);
      if (autoCycleRef.current) { clearInterval(autoCycleRef.current); autoCycleRef.current = null; }
      if (mapInstanceRef.current) mapInstanceRef.current.stop();
    }
  }, [isFullscreen]);

  // ── War Room: Pause Intelligence — instantly kill all timers + animations ──
  useEffect(() => {
    if (intelligencePaused) {
      if (spotlightTimeoutRef.current) { clearTimeout(spotlightTimeoutRef.current); spotlightTimeoutRef.current = null; }
      if (autoCycleRef.current) { clearInterval(autoCycleRef.current); autoCycleRef.current = null; }
      setSpotlightLoc(null);
      if (mapInstanceRef.current) mapInstanceRef.current.stop();
    }
  }, [intelligencePaused]);

  // ── War Room: Event Spotlight — react only to heartbeat-fed liveFeed changes ──
  useEffect(() => {
    if (!isFullscreen || intelligencePaused || !settings.show_sales_events || !settings.spotlight_enabled || liveFeed.length === 0) return;

    const latestEvent = liveFeed[0];
    if (!latestEvent || lastSpotlightEventIdRef.current === latestEvent.id || !mapInstanceRef.current) return;

    const loc = internal.find((branch) => branch.id === latestEvent.locationId);
    if (!loc) return;

    lastSpotlightEventIdRef.current = latestEvent.id;
    setSpotlightLoc({ loc, delta: latestEvent.delta });
    mapInstanceRef.current.stop();
    mapInstanceRef.current.flyTo([latestEvent.lat, latestEvent.lon], 15, { duration: 2 });

    if (spotlightTimeoutRef.current) clearTimeout(spotlightTimeoutRef.current);
    spotlightTimeoutRef.current = setTimeout(() => {
      setSpotlightLoc(null);
      if (mapInstanceRef.current) {
        mapInstanceRef.current.stop();
        mapInstanceRef.current.setView(
          [settings.default_center_lat, settings.default_center_lon],
          settings.default_zoom,
          { animate: true },
        );
      }
    }, 8000);
  }, [
    liveFeed,
    internal,
    isFullscreen,
    intelligencePaused,
    settings.default_center_lat,
    settings.default_center_lon,
    settings.default_zoom,
    settings.show_sales_events,
    settings.spotlight_enabled,
  ]);

  // ── War Room: Event-Driven Auto-Cycle Camera ───────────
  // Only flies to a store when there is a NEW live event. Otherwise stays on overview.
  useEffect(() => {
    if (!isFullscreen || !settings.auto_cycle_enabled || intelligencePaused) {
      if (autoCycleRef.current) { clearInterval(autoCycleRef.current); autoCycleRef.current = null; }
      return;
    }

    const intervalMs = (settings.auto_cycle_interval || 60) * 1000;
    let lastProcessedEventCount = warRoomEventsRef.current.length;

    const cycle = () => {
      if (spotlightActiveRef.current || !mapInstanceRef.current) return;
      const currentEvents = warRoomEventsRef.current;
      const hasNewEvents = currentEvents.length > lastProcessedEventCount;
      lastProcessedEventCount = currentEvents.length;

      if (hasNewEvents && currentEvents.length > 0) {
        // Fly to the latest event location when auto-cycle is enabled.
        const latest = currentEvents[0];
        mapInstanceRef.current.flyTo([latest.lat, latest.lon], 15, { duration: 2 });
      } else {
        // No new events — hold the saved map overview without fitBounds jumps.
        mapInstanceRef.current.setView(
          [settings.default_center_lat, settings.default_center_lon],
          settings.default_zoom,
          { animate: true },
        );
      }
    };

    autoCycleRef.current = setInterval(cycle, intervalMs);
    return () => { if (autoCycleRef.current) clearInterval(autoCycleRef.current); };
  }, [isFullscreen, settings.auto_cycle_enabled, settings.auto_cycle_interval, intelligencePaused]);

  // AI Market Scout handler
  const handleScout = async (loc: MapLocation) => {
    if (scouting) return;
    setScouting(true);
    setScoutTarget(loc);
    setScoutedResults([]);
    try {
      const radius = loc.service_radius || settings.default_service_radius || 3000;
      const res: any = await apiFetch('/api/map/scout', {
        method: 'POST',
        body: JSON.stringify({ lat: loc.lat, lng: loc.lon, radius, keyword: 'restaurant' }),
      });
      const results: ScoutedCompetitor[] = res.data ?? [];
      const existing = locations.filter(l => l.type === 'COMPETITOR');
      const filtered = results.filter(r => {
        return !existing.some(e => Math.abs(e.lat - r.lat) < 0.0005 && Math.abs(e.lon - r.lng) < 0.0005);
      });
      setScoutedResults(filtered);
      toast.success('Scout Complete', `Found ${filtered.length} new competitor${filtered.length !== 1 ? 's' : ''} nearby.`);
    } catch (e: any) {
      toast.error('Scout Failed', e.message || 'Could not discover competitors.');
    } finally {
      setScouting(false);
    }
  };

  const handleImportScouted = async (comp: ScoutedCompetitor) => {
    setImportingPlaceId(comp.placeId);
    try {
      await apiFetch('/api/map/locations', {
        method: 'POST',
        body: JSON.stringify({
          name: comp.name, type: 'COMPETITOR', lat: comp.lat, lon: comp.lng,
          competitorTag: 'Google Discovery',
          description: `⭐ ${comp.rating} (${comp.userRatingsTotal} reviews) — ${comp.vicinity}`,
          address: comp.vicinity, google_rating: comp.rating, google_place_id: comp.placeId,
        }),
      });
      toast.success('Imported', `${comp.name} added to your map.`);
      setScoutedResults(prev => prev.filter(r => r.placeId !== comp.placeId));
      loadData();
    } catch (e: any) {
      toast.error('Import Failed', e.message || 'Could not save competitor.');
    } finally {
      setImportingPlaceId(null);
    }
  };

  const clearScoutResults = () => { setScoutedResults([]); setScoutTarget(null); };

  const focusLocation = (loc: MapLocation) => {
    setSelectedLoc(loc);
    if (mapInstanceRef.current) {
      mapInstanceRef.current.flyTo([loc.lat, loc.lon], 16, { duration: 0.8 });
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <Loader2 size={28} className="animate-spin text-indigo-400" />
        <p className="text-sm text-slate-400 font-medium">Loading map data…</p>
      </div>
    );
  }

  if (error && locations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <AlertTriangle size={28} className="text-amber-400" />
        <p className="text-sm text-slate-500 font-medium">Failed to load map — database may be warming up.</p>
        <button onClick={() => loadData()}
          className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white rounded-lg text-xs font-bold hover:bg-indigo-700 transition-colors">
          <RefreshCw size={13} /> Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header — hidden in fullscreen */}
      {!isFullscreen && (
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-slate-900">Live Map</h2>
            <p className="text-sm text-slate-500 mt-1">Real-time branch performance visualization.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => loadData()} className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-600 hover:border-indigo-300 transition-colors">
              <RefreshCw size={13} /> Refresh
            </button>
          </div>
        </div>
      )}

      {/* Map Container — supports native fullscreen */}
      <div ref={containerRef}
        className={cn(
          'relative bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm',
          isFullscreen && 'rounded-none border-0 shadow-none'
        )}
        style={isFullscreen
          ? isPortrait
            ? { position: 'fixed', top: 0, left: 0, width: '100vh', height: '100vw', transform: 'rotate(-90deg) translateX(-100%)', transformOrigin: 'top left', overflow: 'hidden' }
            : { width: '100%', height: '100%' }
          : { height: 'calc(100vh - 260px)', minHeight: 500 }
        }>

        <MapRenderer
          locations={visibleLocations}
          allLocations={locations}
          settings={settings}
          showPerformance={showPerformance}
          showLabels={showLabels}
          showCoverage={showCoverage}
          onSelectLocation={setSelectedLoc}
          selectedLocation={selectedLoc}
          onMapReady={(m: any) => { mapInstanceRef.current = m; setMapReady(true); }}
          scoutedCompetitors={scoutedResults}
          scoutTarget={scoutTarget}
          scouting={scouting}
          spotlightLocationId={spotlightLoc?.loc.id ?? null}
          onHoverScouted={(comp, pos) => setHoveredScouted({ comp, x: pos.x, y: pos.y })}
          onLeaveScouted={() => setHoveredScouted(null)}
        />

        {/* Lazy-load skeleton — dark curtain for War Room, light for desktop */}
        <AnimatePresence>
          {!mapReady && (
            <motion.div
              initial={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.8 }}
              className={cn(
                'absolute inset-0 z-[1100] flex flex-col items-center justify-center gap-4',
                isFullscreen ? 'bg-slate-900' : 'bg-slate-50',
              )}>
              {isFullscreen ? (
                <>
                  <div className="relative w-20 h-20">
                    <div className="absolute inset-0 rounded-full border-4 border-indigo-500/30 animate-ping" />
                    <div className="absolute inset-3 rounded-full border-4 border-indigo-400/50 animate-pulse" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Monitor size={24} className="text-indigo-400" />
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-bold text-white">Initializing War Room…</p>
                    <p className="text-[11px] text-slate-400 mt-1">Loading branches, KPIs & live intelligence</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="relative w-16 h-16">
                    <div className="absolute inset-0 rounded-full border-4 border-slate-200 animate-ping opacity-30" />
                    <div className="absolute inset-2 rounded-full border-4 border-indigo-300 animate-pulse" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <MapPin size={20} className="text-indigo-500" />
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-bold text-slate-600">Initializing Market Data…</p>
                    <p className="text-[11px] text-slate-400 mt-1">Loading branches, competitors & performance</p>
                  </div>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Floating Legend */}
        <MapLegend />

        {/* Layer Controls + Fullscreen Button */}
        <div className="absolute top-4 right-4 z-[1001] flex flex-col items-end gap-2">
          <div className="flex items-center gap-2">
            <button onClick={toggleFullscreen}
              title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
              className="w-10 h-10 bg-white border border-slate-200 rounded-xl flex items-center justify-center text-slate-600 hover:text-slate-900 hover:border-slate-300 transition-colors shadow-sm">
              {isFullscreen ? <Minimize size={16} /> : <Maximize size={16} />}
            </button>
            <LayerControls
              showInternal={showInternal} setShowInternal={setShowInternal}
              showCompetitors={showCompetitors} setShowCompetitors={setShowCompetitors}
              showPerformance={showPerformance} setShowPerformance={setShowPerformance}
              showLabels={showLabels} setShowLabels={setShowLabels}
              showCoverage={showCoverage} setShowCoverage={setShowCoverage}
              competitorTags={competitorTags} hiddenTagIds={hiddenTagIds} setHiddenTagIds={setHiddenTagIds}
            />
          </div>
        </div>

        {/* Fullscreen Refresh + Exit bar */}
        {isFullscreen && (
          <div className="absolute top-4 left-4 z-[1001] flex items-center gap-2">
            <button onClick={() => loadData()}
              className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-600 hover:border-indigo-300 transition-colors shadow-sm">
              <RefreshCw size={13} /> Refresh
            </button>
          </div>
        )}

        {/* ═══ WAR ROOM OVERLAYS (fullscreen only) ═══ */}
        {isFullscreen && (
          <>
            {/* KPI Header — top center */}
            <div className={cn('absolute z-[999]', isPortrait ? 'top-4 left-4 right-4' : 'top-4 left-1/2 -translate-x-1/2')}>
              <div className="flex flex-col items-center gap-1.5">
                <div className="flex items-center gap-3 bg-white/95 backdrop-blur-sm border border-slate-200 rounded-2xl px-5 py-2.5 shadow-lg flex-wrap justify-center">
                <div className="flex items-center gap-2">
                  <TrendingUp size={14} className="text-indigo-500" />
                  <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Org Sales</span>
                </div>
                <div className="h-5 w-px bg-slate-200" />
                <span className="text-sm font-black text-slate-900">QAR {formatCurrency(totalSales)}</span>
                <span className="text-xs text-slate-400">/</span>
                <span className="text-sm font-bold text-slate-500">{formatCurrency(totalTarget)}</span>
                <div className="h-5 w-px bg-slate-200" />
                <span className={cn('text-sm font-black', overallRatio >= 0.8 ? 'text-emerald-600' : overallRatio >= 0.4 ? 'text-amber-600' : 'text-red-600')}>
                  {(overallRatio * 100).toFixed(1)}%
                </span>
                <div className="h-5 w-px bg-slate-200" />
                <div className="flex items-center gap-2 text-[11px] font-bold">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" />{greenCount}</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" />{amberCount}</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" />{redCount}</span>
                </div>
                {settings.auto_cycle_enabled ? (
                  <>
                    <div className="h-5 w-px bg-slate-200" />
                    <button onClick={() => setIntelligencePaused(p => !p)}
                      className={cn('flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold transition-colors',
                        intelligencePaused ? 'bg-amber-50 text-amber-600 border border-amber-200' : 'bg-indigo-50 text-indigo-600 border border-indigo-200 hover:bg-indigo-100')}>
                      {intelligencePaused ? <><Play size={10} /> Resume</> : <><Pause size={10} /> Pause</>}
                    </button>
                  </>
                ) : null}
                </div>
                {/* Brand-Wise KPI Pills */}
                {brandKpis.length > 1 && (
                  <div className="flex items-center gap-1.5 flex-wrap justify-center">
                    {brandKpis.map(b => {
                      const color = b.ratio >= 0.8 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : b.ratio >= 0.4 ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-red-50 text-red-700 border-red-200';
                      return (
                        <span key={b.name}
                          className={cn('inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold border backdrop-blur-sm', color)}>
                          {b.name}: {(b.ratio * 100).toFixed(0)}%
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Leaderboard — right (landscape) / bottom (portrait) */}
            <motion.div initial={{ opacity: 0, x: isPortrait ? 0 : 30, y: isPortrait ? 30 : 0 }} animate={{ opacity: 1, x: 0, y: 0 }} transition={{ delay: 0.2 }}
              className={cn('absolute z-[999] bg-white/95 backdrop-blur-sm border border-slate-200 rounded-2xl shadow-lg overflow-y-auto',
                isPortrait
                  ? 'bottom-4 left-4 right-4 max-h-[30%]'
                  : 'top-24 right-8 w-64 max-h-[calc(100%-8rem)]'
              )}>
              <div className="sticky top-0 bg-white/95 backdrop-blur-sm border-b border-slate-100 px-4 py-3 flex items-center gap-2">
                <Trophy size={14} className="text-yellow-500" />
                <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">Leaderboard</span>
                <span className="ml-auto text-[10px] text-slate-400">{internal.filter(l => (l.current_sales ?? 0) > 0).length} active</span>
              </div>
              <div className="p-2 space-y-0.5">
                {(() => {
                  const active = internal.filter(l => (l.current_sales ?? 0) > 0);
                  if (active.length === 0) return (
                    <div className="px-3 py-6 text-center">
                      <Trophy size={18} className="mx-auto text-slate-300 mb-2" />
                      <p className="text-[11px] text-slate-400">Waiting for first sales report…</p>
                      <p className="text-[10px] text-slate-300 mt-1">Active branches will rank here</p>
                    </div>
                  );
                  return [...active]
                    .sort((a, b) => {
                      const aRatio = (a.current_sales ?? 0) / Math.max(1, getEffectiveTarget(a));
                      const bRatio = (b.current_sales ?? 0) / Math.max(1, getEffectiveTarget(b));
                      return bRatio - aRatio;
                    })
                    .slice(0, 10)
                    .map((loc, i) => {
                      const ratio = (loc.current_sales ?? 0) / Math.max(1, getEffectiveTarget(loc));
                      const color = ratio >= 1 ? '#eab308' : getPerformanceColor(ratio);
                      return (
                        <button key={loc.id} onClick={() => focusLocation(loc)}
                          className="w-full flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-slate-50 transition-colors text-left">
                          <span className="text-[11px] font-black text-slate-400 w-5 text-right">{i + 1}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-bold text-slate-800 truncate">{ratio >= 1 ? '🏆 ' : ''}{loc.name}</p>
                            <p className="text-[10px] text-slate-400">QAR {formatCurrency(loc.current_sales ?? 0)}</p>
                          </div>
                          <span className="text-xs font-black shrink-0" style={{ color }}>
                            {(ratio * 100).toFixed(0)}%
                          </span>
                        </button>
                      );
                    });
                })()}
              </div>
            </motion.div>

            {/* Alert Feed — left (landscape) / top-below-KPI (portrait) */}
            <motion.div initial={{ opacity: 0, x: isPortrait ? 0 : -30, y: isPortrait ? -30 : 0 }} animate={{ opacity: 1, x: 0, y: 0 }} transition={{ delay: 0.3 }}
              className={cn('absolute z-[999] bg-white/95 backdrop-blur-sm border border-slate-200 rounded-2xl shadow-lg overflow-y-auto',
                isPortrait
                  ? 'top-28 left-4 right-4 max-h-[25%]'
                  : 'top-24 left-8 w-64 max-h-[calc(100%-8rem)]'
              )}>
              <div className="sticky top-0 bg-white/95 backdrop-blur-sm border-b border-slate-100 px-4 py-3 flex items-center gap-2">
                <Activity size={14} className="text-blue-500" />
                <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">Live Feed</span>
                {warRoomEvents.length > 0 && (
                  <span className="ml-auto flex items-center gap-1 text-[10px] text-emerald-600 font-bold">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Live
                  </span>
                )}
              </div>
              <div className="p-2 space-y-1">
                {warRoomEvents.length === 0 ? (
                  <div className="px-3 py-6 text-center">
                    <Zap size={18} className="mx-auto text-slate-300 mb-2" />
                    <p className="text-[11px] text-slate-400">Waiting for live events…</p>
                    <p className="text-[10px] text-slate-300 mt-1">Sales changes will appear here</p>
                  </div>
                ) : (
                  warRoomEvents.map(evt => (
                    <div key={evt.id} className="flex items-start gap-2 px-3 py-2 rounded-xl bg-slate-50 border border-slate-100">
                      <div className="w-6 h-6 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center shrink-0 mt-0.5">
                        <TrendingUp size={11} className="text-emerald-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] font-bold text-slate-800 truncate">{evt.storeName}</p>
                        <p className="text-[10px] text-slate-600 font-semibold leading-snug">{evt.message}</p>
                        <p className="text-[10px] text-emerald-600 font-bold">+QAR {formatCurrency(evt.delta)}</p>
                        <p className="text-[9px] text-slate-400">{formatTimeAgo(evt.timestamp)}</p>
                      </div>
                      <span className="text-[10px] font-bold text-slate-500 shrink-0">
                        {formatCurrency(evt.newTotal)}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </motion.div>

            {/* Event Spotlight Toast — z-[1200] guarantees it renders above all map layers and overlays */}
            <AnimatePresence>
              {spotlightLoc && (
                <motion.div
                  initial={{ opacity: 0, y: 30, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 30, scale: 0.95 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                  className="absolute bottom-24 left-1/2 -translate-x-1/2 z-[1200]">
                  <div className="flex items-center gap-3 bg-white border-2 border-emerald-400 rounded-2xl px-5 py-3 shadow-2xl">
                    <div className="w-8 h-8 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center shrink-0">
                      <Zap size={16} className="text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm font-black text-slate-900">{spotlightLoc.loc.name}</p>
                      <p className="text-xs text-emerald-600 font-bold">📈 New Sale +QAR {formatCurrency(spotlightLoc.delta)}</p>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}

        {/* Selected Location Info */}
        {selectedLoc && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-[1001]">
            <LocationInfoCard location={selectedLoc} onClose={() => setSelectedLoc(null)}
              onScout={handleScout} scouting={scouting} scoutEnabled={isAdmin && !!settings.ai_google_places} />
          </div>
        )}

        {/* Scouted Competitor Hover Tooltip */}
        {hoveredScouted && (
          <div className="fixed z-[9999] pointer-events-none" style={{ left: hoveredScouted.x + 12, top: hoveredScouted.y - 60 }}>
            <div className="bg-slate-900/90 backdrop-blur-xl border border-slate-700 rounded-xl px-4 py-3 min-w-[200px] shadow-2xl">
              <p className="text-sm font-bold text-white">{hoveredScouted.comp.name}</p>
              <p className="text-[11px] text-yellow-400 mt-0.5">⭐ {hoveredScouted.comp.rating} ({hoveredScouted.comp.userRatingsTotal} reviews)</p>
              {hoveredScouted.comp.vicinity && <p className="text-[10px] text-white/40 mt-1">{hoveredScouted.comp.vicinity}</p>}
            </div>
          </div>
        )}

        {/* Scout Results Panel */}
        <AnimatePresence>
          {scoutedResults.length > 0 && (
            <motion.div initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 40 }}
              className="absolute top-16 right-4 z-[1002] w-72 max-h-[calc(100%-5rem)] overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-lg">
              <div className="sticky top-0 bg-slate-50 border-b border-slate-200 px-4 py-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold text-slate-900 flex items-center gap-1.5"><Radar size={13} className="text-blue-500" /> Scout Results</p>
                  <p className="text-[10px] text-slate-400">{scoutedResults.length} competitor{scoutedResults.length !== 1 ? 's' : ''} discovered</p>
                </div>
                <button onClick={clearScoutResults} className="p-1 hover:bg-slate-100 rounded-lg transition-colors">
                  <X size={14} className="text-slate-400" />
                </button>
              </div>
              <div className="p-2 space-y-1">
                {scoutedResults.map(comp => (
                  <div key={comp.placeId} className="bg-slate-50 border border-slate-100 rounded-lg p-3 hover:border-slate-200 transition-colors">
                    <p className="text-xs font-bold text-slate-900 truncate">{comp.name}</p>
                    <p className="text-[10px] text-yellow-600 mt-0.5">⭐ {comp.rating} ({comp.userRatingsTotal} reviews)</p>
                    {comp.vicinity && <p className="text-[10px] text-slate-400 mt-0.5 truncate">{comp.vicinity}</p>}
                    <button
                      onClick={() => handleImportScouted(comp)}
                      disabled={importingPlaceId === comp.placeId}
                      className="mt-2 w-full flex items-center justify-center gap-1.5 px-2 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-[10px] font-bold transition-all disabled:opacity-50"
                    >
                      {importingPlaceId === comp.placeId ? (
                        <><Loader2 size={11} className="animate-spin" /> Importing…</>
                      ) : (
                        <>📍 Add to My Map</>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Footer — hidden in fullscreen */}
      {!isFullscreen && (
        <p className="text-[10px] text-slate-400 text-center">Map auto-refreshes every 30 seconds. Sales data updates in real-time as managers enter figures.</p>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// MAP RENDERER — Light Tiles Only, Auto-Fit Bounds
// ═══════════════════════════════════════════════════════════

function MapRenderer({
  locations, allLocations, settings, showPerformance, showLabels, showCoverage,
  onSelectLocation, selectedLocation, onMapReady,
  scoutedCompetitors, scoutTarget, scouting, onHoverScouted, onLeaveScouted,
  spotlightLocationId,
}: {
  locations: MapLocation[];
  allLocations: MapLocation[];
  settings: MapSettings;
  showPerformance: boolean;
  showLabels: boolean;
  showCoverage: boolean;
  onSelectLocation: (loc: MapLocation | null) => void;
  selectedLocation: MapLocation | null;
  onMapReady?: (map: any) => void;
  scoutedCompetitors?: ScoutedCompetitor[];
  scoutTarget?: MapLocation | null;
  scouting?: boolean;
  onHoverScouted?: (comp: ScoutedCompetitor, e: { x: number; y: number }) => void;
  onLeaveScouted?: () => void;
  spotlightLocationId?: string | null;
}) {
  const mapRef = useRef<HTMLDivElement>(null);
  const leafletMap = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const circlesRef = useRef<any[]>([]);
  const markerMapRef = useRef<Map<string, any>>(new Map()); // loc.id → leaflet marker
  const markerBaseZIndexRef = useRef<Map<string, number>>(new Map());
  const [leafletReady, setLeafletReady] = useState(false);
  const LRef = useRef<any>(null);
  const hasFitBounds = useRef(false);

  // Dynamic import
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const L = await import('leaflet');
      await import('leaflet/dist/leaflet.css');
      if (cancelled) return;
      LRef.current = L.default || L;
      setLeafletReady(true);
    })();
    return () => { cancelled = true; };
  }, []);

  // Initialize map — ALWAYS Silver/Light tiles
  useEffect(() => {
    if (!leafletReady || !mapRef.current || leafletMap.current) return;
    const L = LRef.current;

    const m = L.map(mapRef.current, {
      center: [settings.default_center_lat, settings.default_center_lon],
      zoom: settings.default_zoom,
      zoomControl: true,
      attributionControl: true,
    });

    L.tileLayer(SILVER_TILE, { attribution: TILE_ATTR, maxZoom: 18 }).addTo(m);

    leafletMap.current = m;
    if (onMapReady) onMapReady(m);

    return () => { m.remove(); leafletMap.current = null; };
  }, [leafletReady]);

  // Re-apply saved center + zoom when settings load from API
  const settingsApplied = useRef(false);
  useEffect(() => {
    if (!leafletMap.current || settingsApplied.current) return;
    // Always apply once settings have an org_id (i.e. loaded from API)
    if (settings.org_id) {
      leafletMap.current.setView(
        [settings.default_center_lat, settings.default_center_lon],
        settings.default_zoom,
        { animate: false },
      );
      settingsApplied.current = true;
    }
  }, [settings]);

  // Update markers + circles + auto-fit on first data load
  useEffect(() => {
    if (!leafletReady || !leafletMap.current) return;
    const L = LRef.current;
    const m = leafletMap.current;

    markersRef.current.forEach(mk => m.removeLayer(mk));
    markersRef.current = [];
    circlesRef.current.forEach(c => m.removeLayer(c));
    circlesRef.current = [];
    markerMapRef.current.clear();
    markerBaseZIndexRef.current.clear();

    const defaultRadius = settings.default_service_radius || 3000;
    const internalLocs = locations.filter(l => l.type === 'INTERNAL');
    const competitorLocs = allLocations.filter(l => l.type === 'COMPETITOR');

    const threatMap: Record<string, boolean> = {};
    const overlapMap: Record<string, boolean> = {};
    internalLocs.forEach(loc => {
      const r = loc.service_radius || defaultRadius;
      competitorLocs.forEach(comp => {
        const d = geoDistanceMeters(loc.lat, loc.lon, comp.lat, comp.lon);
        if (d < r) threatMap[loc.id] = true;
      });
      internalLocs.forEach(other => {
        if (other.id === loc.id) return;
        const r2 = other.service_radius || defaultRadius;
        const dist = geoDistanceMeters(loc.lat, loc.lon, other.lat, other.lon);
        if (dist < r + r2) { overlapMap[loc.id] = true; overlapMap[other.id] = true; }
      });
    });

    if (showCoverage) {
      internalLocs.forEach(loc => {
        const r = loc.service_radius || defaultRadius;
        if (r <= 0) return;
        const hasThreat = threatMap[loc.id];
        const hasOverlap = overlapMap[loc.id];
        const circle = L.circle([loc.lat, loc.lon], {
          radius: r,
          color: hasThreat ? '#ef4444' : hasOverlap ? '#f97316' : '#6366f1',
          weight: hasThreat ? 2 : 1.5,
          dashArray: hasOverlap && !hasThreat ? '8 4' : undefined,
          fillColor: hasThreat ? '#ef4444' : hasOverlap ? '#f97316' : '#6366f1',
          fillOpacity: 0.06,
          interactive: false,
        }).addTo(m);
        circlesRef.current.push(circle);
      });
    }

    locations.forEach(loc => {
      if (loc.type === 'COMPETITOR') {
        const tag = loc.tag_name || loc.competitor_tag || 'Competitor';
        const tagColor = loc.tag_color || '#ef4444';
        const initial = tag.charAt(0).toUpperCase();
        const logoUrl = getMediaUrl(loc.tag_logo_url);
        const nameLabel = showLabels
          ? `<div style="position:absolute;top:34px;left:50%;transform:translateX(-50%);white-space:nowrap;background:#fff;color:${tagColor};font-size:9px;font-weight:800;padding:1px 5px;border-radius:4px;box-shadow:0 1px 4px rgba(0,0,0,0.15);border:1px solid ${tagColor}40;">${loc.name}</div>`
          : '';
        const pinContent = logoUrl
          ? `<img src="${logoUrl}" style="width:28px;height:28px;border-radius:50%;object-fit:cover;border:2px solid ${tagColor};box-shadow:0 2px 6px rgba(0,0,0,0.15);" onerror="this.style.display='none';this.nextSibling.style.display='flex';" /><div style="display:none;width:28px;height:28px;border-radius:4px;background:#f8fafc;border:2px solid ${tagColor};align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(0,0,0,0.15);"><span style="color:${tagColor};font-size:12px;font-weight:900;">${initial}</span></div>`
          : `<div style="width:28px;height:28px;border-radius:4px;background:#f8fafc;border:2px solid ${tagColor};display:flex;align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(0,0,0,0.15);"><span style="color:${tagColor};font-size:12px;font-weight:900;">${initial}</span></div>`;
        const icon = L.divIcon({
          className: '',
          html: `<div style="position:relative;width:28px;height:28px;">${pinContent}${nameLabel}</div>`,
          iconSize: [28, showLabels ? 50 : 28],
          iconAnchor: [14, 14],
        });
        const marker = L.marker([loc.lat, loc.lon], { icon, zIndexOffset: 0 }).addTo(m);
        marker.on('click', () => onSelectLocation(loc));
        markerMapRef.current.set(loc.id, marker);
        markerBaseZIndexRef.current.set(loc.id, 0);
        markersRef.current.push(marker);
      } else {
        const ratio = getSalesRatio(loc);
        const isChampion = ratio >= 1.0;
        const color = isChampion ? '#eab308' : (showPerformance ? getPerformanceColor(ratio) : '#6366f1');
        const pulseSpeed = isChampion ? '2.5s' : (showPerformance ? getPulseSpeed(ratio) : '3s');
        const sales = loc.current_sales ?? 0;
        const hasSales = sales > 0;
        const baseZIndex = isChampion ? 10000 : hasSales ? 1000 + Math.round(Number(sales)) : 0;
        const size = 40;
        const logoUrl = getMediaUrl(loc.brand_logo_url);

        const logoHtml = logoUrl
          ? `<img src="${logoUrl}" style="width:28px;height:28px;border-radius:50%;object-fit:cover;border:2px solid ${color};box-shadow:0 0 ${isChampion ? '12' : '6'}px ${color}${isChampion ? '' : '60'};" />`
          : `<div style="width:28px;height:28px;border-radius:50%;background:#f8fafc;border:2px solid ${color};display:flex;align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(0,0,0,0.15);">
               <span style="color:${color};font-size:10px;font-weight:900;">${(loc.name || '?').charAt(0)}</span>
             </div>`;

        const pulseRing = showPerformance && hasSales
          ? `<div style="position:absolute;inset:-3px;border-radius:50%;border:2px solid ${color};opacity:${isChampion ? '0.8' : '0.6'};animation:pulse-ring ${pulseSpeed} ease-out infinite;${isChampion ? 'box-shadow:0 0 10px ' + color + ';' : ''}"></div>`
          : '';

        const salesLabel = hasSales
          ? `<div style="position:absolute;bottom:-12px;left:50%;transform:translateX(-50%);white-space:nowrap;background:${color};color:${isChampion ? '#000' : '#fff'};font-size:9px;font-weight:800;padding:1px 4px;border-radius:4px;box-shadow:0 1px 3px rgba(0,0,0,0.2);">${isChampion ? '🏆 ' : ''}QAR ${formatCurrency(sales)}</div>`
          : '';

        const nameLabel = showLabels
          ? `<div style="position:absolute;top:-14px;left:50%;transform:translateX(-50%);white-space:nowrap;background:#fff;color:#334155;font-size:9px;font-weight:700;padding:1px 5px;border-radius:4px;box-shadow:0 1px 3px rgba(0,0,0,0.15);border:1px solid ${color}40;">${isChampion ? '🏆 ' : ''}${loc.name}</div>`
          : '';

        const icon = L.divIcon({
          className: '',
          html: `<div style="position:relative;width:${size}px;height:${size}px;display:flex;align-items:center;justify-content:center;">
            ${pulseRing}
            <div style="position:relative;z-index:1;">${logoHtml}</div>
            ${salesLabel}
            ${nameLabel}
          </div>`,
          iconSize: [size, size + 12],
          iconAnchor: [size / 2, size / 2],
        });
        const marker = L.marker([loc.lat, loc.lon], { icon, zIndexOffset: spotlightLocationId === loc.id ? 10000 : baseZIndex }).addTo(m);
        marker.on('click', () => onSelectLocation(loc));
        markerMapRef.current.set(loc.id, marker);
        markerBaseZIndexRef.current.set(loc.id, baseZIndex);
        markersRef.current.push(marker);
      }
    });

    // Re-apply spotlight z-index if a spotlight was active when markers were recreated.
    if (spotlightLocationId) {
      const sm = markerMapRef.current.get(spotlightLocationId);
      if (sm) {
        sm.setZIndexOffset(10000);
        const el = sm.getElement();
        if (el) {
          el.style.filter = 'drop-shadow(0 0 10px #10b981) drop-shadow(0 0 20px rgba(16,185,129,0.5))';
          el.style.transition = 'filter 0.4s ease, transform 0.4s ease';
          el.style.transform = 'scale(1.08)';
        }
      }
    }

    // Scouted competitor pins
    if (scoutedCompetitors && scoutedCompetitors.length > 0) {
      const internalLocs2 = locations.filter(l => l.type === 'INTERNAL');
      const avgInternalRating = internalLocs2.length > 0
        ? internalLocs2.reduce((s, l) => s + (l.google_rating || 0), 0) / internalLocs2.length : 4.0;

      scoutedCompetitors.forEach(comp => {
        const isHighThreat = comp.rating > avgInternalRating && comp.rating >= 4.0;
        const icon = L.divIcon({
          className: '',
          html: `<div style="position:relative;width:32px;height:32px;">
            <div style="position:absolute;inset:0;border-radius:50%;background:#f8fafc;border:2px solid ${isHighThreat ? '#ef4444' : '#94a3b8'};display:flex;align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(0,0,0,0.15);${isHighThreat ? 'animation:scout-pulse 1.5s ease-in-out infinite;' : ''}">
              <span style="color:${isHighThreat ? '#ef4444' : '#64748b'};font-size:11px;font-weight:900;">📍</span>
            </div>
            <div style="position:absolute;top:34px;left:50%;transform:translateX(-50%);white-space:nowrap;background:#fff;color:#334155;font-size:7px;font-weight:700;padding:1px 5px;border-radius:4px;box-shadow:0 1px 3px rgba(0,0,0,0.15);border:1px solid ${isHighThreat ? '#fca5a540' : '#e2e8f0'};">
              ⭐ ${comp.rating} · ${comp.name.slice(0, 18)}${comp.name.length > 18 ? '…' : ''}
            </div>
          </div>`,
          iconSize: [32, 52],
          iconAnchor: [16, 16],
        });
        const marker = L.marker([comp.lat, comp.lng], { icon }).addTo(m);
        marker.on('mouseover', (e: any) => {
          if (onHoverScouted) onHoverScouted(comp, { x: e.originalEvent.clientX, y: e.originalEvent.clientY });
        });
        marker.on('mouseout', () => { if (onLeaveScouted) onLeaveScouted(); });
        markersRef.current.push(marker);
      });
    }

    // Radar animation while scouting
    if (scouting && scoutTarget) {
      const radarRadius = scoutTarget.service_radius || defaultRadius;
      const radarCircle = L.circle([scoutTarget.lat, scoutTarget.lon], {
        radius: radarRadius, color: '#3b82f6', weight: 2, dashArray: '6 4',
        fillColor: '#3b82f6', fillOpacity: 0.08, interactive: false, className: 'radar-pulse',
      }).addTo(m);
      circlesRef.current.push(radarCircle);
    }

    // Auto-fit bounds on first data load — but ONLY if user has NOT saved custom center/zoom
    // When settings come from D1 (org_id is set), respect saved center/zoom instead.
    if (!hasFitBounds.current && locations.length > 0) {
      hasFitBounds.current = true;
      if (!settings.org_id) {
        // No saved settings → auto-fit to show all pins
        const allCoords = locations.map(l => [l.lat, l.lon] as [number, number]);
        if (allCoords.length > 0) {
          m.fitBounds(L.latLngBounds(allCoords), { padding: [40, 40], maxZoom: 12 });
        }
      }
    }
  }, [leafletReady, locations, allLocations, showPerformance, showLabels, showCoverage, settings.default_service_radius, scoutedCompetitors, scouting, scoutTarget]);

  // ── Pin Z-Index & Glow — preserve sales hierarchy and lift spotlighted pins ──
  useEffect(() => {
    const markerMap = markerMapRef.current;
    // Reset all markers to their sales-driven base z-index and remove spotlight styling.
    markerMap.forEach((mk, locationId) => {
      mk.setZIndexOffset(markerBaseZIndexRef.current.get(locationId) ?? 0);
      const el = mk.getElement();
      if (el) {
        el.style.filter = '';
        el.style.transition = '';
        el.style.transform = '';
      }
    });

    // Apply spotlight to the active marker.
    if (spotlightLocationId) {
      const sm = markerMap.get(spotlightLocationId);
      if (sm) {
        sm.setZIndexOffset(10000);
        const el = sm.getElement();
        if (el) {
          el.style.transition = 'filter 0.4s ease, transform 0.4s ease';
          el.style.filter = 'drop-shadow(0 0 10px #10b981) drop-shadow(0 0 20px rgba(16,185,129,0.5))';
          el.style.transform = 'scale(1.08)';
        }
      }
    }
  }, [spotlightLocationId]);

  return (
    <div ref={mapRef} className="w-full h-full" style={{ background: '#f0f0f0' }}>
      {!leafletReady && (
        <div className="flex items-center justify-center h-full">
          <Loader2 size={24} className="animate-spin text-slate-400" />
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// FLOATING UI — White/Light Theme Only
// ═══════════════════════════════════════════════════════════

function MapLegend() {
  return (
    <div className="absolute bottom-4 left-4 z-[1001] bg-white/95 backdrop-blur-sm border border-slate-200 rounded-xl px-4 py-3 space-y-1.5 shadow-sm">
      <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Legend</p>
      <div className="flex items-center gap-2">
        <span className="w-3 h-3 rounded-full bg-yellow-500" />
        <span className="text-[11px] text-slate-700">🏆 Target Achieved (≥100%)</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="w-3 h-3 rounded-full bg-emerald-500" />
        <span className="text-[11px] text-slate-700">On Target (≥80%)</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="w-3 h-3 rounded-full bg-amber-500" />
        <span className="text-[11px] text-slate-700">Near Target (40–80%)</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="w-3 h-3 rounded-full bg-red-500" />
        <span className="text-[11px] text-slate-700">Critical (&lt;40%)</span>
      </div>
      <div className="flex items-center gap-2 pt-1 border-t border-slate-100">
        <div className="w-3 h-3 rounded bg-slate-100 border border-red-500 flex items-center justify-center">
          <span className="text-red-500 text-[7px] font-black">C</span>
        </div>
        <span className="text-[11px] text-slate-700">Competitor</span>
      </div>
      <div className="flex items-center gap-2 pt-1 border-t border-slate-100">
        <span className="w-3 h-3 rounded-full border-2 border-dashed border-orange-500 bg-transparent" />
        <span className="text-[11px] text-slate-700">Internal Overlap</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="w-3 h-3 rounded-full border-2 border-red-500 bg-transparent" />
        <span className="text-[11px] text-slate-700">Competitive Threat</span>
      </div>
    </div>
  );
}

function LayerControls({
  showInternal, setShowInternal,
  showCompetitors, setShowCompetitors,
  showPerformance, setShowPerformance,
  showLabels, setShowLabels,
  showCoverage, setShowCoverage,
  competitorTags, hiddenTagIds, setHiddenTagIds,
}: {
  showInternal: boolean; setShowInternal: (v: boolean) => void;
  showCompetitors: boolean; setShowCompetitors: (v: boolean) => void;
  showPerformance: boolean; setShowPerformance: (v: boolean) => void;
  showLabels: boolean; setShowLabels: (v: boolean) => void;
  showCoverage: boolean; setShowCoverage: (v: boolean) => void;
  competitorTags?: CompetitorTag[];
  hiddenTagIds?: Set<string>;
  setHiddenTagIds?: (fn: (prev: Set<string>) => Set<string>) => void;
}) {
  const [open, setOpen] = useState(false);
  const layerPanelRef = useRef<HTMLDivElement>(null);

  // Click-outside to close
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (layerPanelRef.current && !layerPanelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="relative" ref={layerPanelRef}>
      <button onClick={() => setOpen(!open)}
        className="w-10 h-10 bg-white border border-slate-200 rounded-xl flex items-center justify-center text-slate-600 hover:text-slate-900 hover:border-slate-300 transition-colors shadow-sm">
        <Layers size={16} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 8 }}
            className="absolute right-12 top-0 bg-white border border-slate-200 rounded-xl p-3 space-y-2 min-w-[220px] shadow-lg">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Layers</p>
            {[
              { label: 'Internal Branches', value: showInternal, set: setShowInternal },
              { label: 'Competitors', value: showCompetitors, set: setShowCompetitors },
              { label: 'Sales Performance', value: showPerformance, set: setShowPerformance },
              { label: 'Store Names', value: showLabels, set: setShowLabels },
              { label: 'Coverage Radius', value: showCoverage, set: setShowCoverage },
            ].map(l => (
              <button key={l.label} onClick={() => l.set(!l.value)}
                className="w-full flex items-center justify-between px-2 py-1.5 rounded-lg hover:bg-slate-50 transition-colors">
                <span className="text-xs text-slate-700">{l.label}</span>
                {l.value
                  ? <ToggleRight size={18} className="text-indigo-500" />
                  : <ToggleLeft size={18} className="text-slate-300" />
                }
              </button>
            ))}
            {showCompetitors && competitorTags && competitorTags.length > 0 && hiddenTagIds && setHiddenTagIds && (
              <>
                <div className="border-t border-slate-100 pt-2 mt-1">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">By Threat Type</p>
                </div>
                {competitorTags.map(tag => {
                  const isVisible = !hiddenTagIds.has(tag.id);
                  return (
                    <button key={tag.id} onClick={() => setHiddenTagIds(prev => {
                      const next = new Set(prev);
                      if (next.has(tag.id)) next.delete(tag.id); else next.add(tag.id);
                      return next;
                    })}
                      className="w-full flex items-center justify-between px-2 py-1 rounded-lg hover:bg-slate-50 transition-colors">
                      <div className="flex items-center gap-2">
                        {tag.logo_url ? (
                          <img src={getMediaUrl(tag.logo_url)} alt="" className="w-4 h-4 rounded-full object-cover" />
                        ) : (
                          <div className="w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-black text-white" style={{ background: tag.color || '#ef4444' }}>
                            {tag.name.charAt(0)}
                          </div>
                        )}
                        <span className="text-[11px] text-slate-600">{tag.name}</span>
                      </div>
                      {isVisible
                        ? <ToggleRight size={16} className="text-indigo-500" />
                        : <ToggleLeft size={16} className="text-slate-300" />
                      }
                    </button>
                  );
                })}
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function LocationInfoCard({ location: loc, onClose, onScout, scouting, scoutEnabled }: {
  location: MapLocation; onClose: () => void;
  onScout?: (loc: MapLocation) => void;
  scouting?: boolean;
  scoutEnabled?: boolean;
}) {
  const ratio = getSalesRatio(loc);
  const color = getPerformanceColor(ratio);
  const isComp = loc.type === 'COMPETITOR';
  const target = getEffectiveTarget(loc);
  const logoUrl = getMediaUrl(loc.brand_logo_url);
  const tagLogoUrl = getMediaUrl(loc.tag_logo_url);
  const tagColor = loc.tag_color || '#ef4444';

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
      className="bg-white border border-slate-200 rounded-2xl px-5 py-4 min-w-[300px] max-w-[400px] shadow-lg">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-3">
          {!isComp && logoUrl ? (
            <img src={logoUrl} alt="" className="w-10 h-10 rounded-full object-cover border-2 shrink-0" style={{ borderColor: color }} />
          ) : isComp && tagLogoUrl ? (
            <img src={tagLogoUrl} alt="" className="w-10 h-10 rounded-full object-cover border-2 shrink-0" style={{ borderColor: tagColor }} />
          ) : (
            <div className={cn('w-10 h-10 rounded-full flex items-center justify-center shrink-0', isComp ? 'bg-red-50' : 'bg-slate-100')}>
              {isComp ? <Flag size={16} className="text-red-500" /> : <Building2 size={16} className="text-slate-500" />}
            </div>
          )}
          <div>
            <p className="text-sm font-bold text-slate-900">{loc.name}</p>
            <p className="text-[10px] text-slate-400 uppercase tracking-wider">
              {isComp ? (loc.tag_name || loc.competitor_tag || 'Competitor') : (loc.brand_name || 'Branch')}
            </p>
          </div>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-lg transition-colors">
          <X size={14} className="text-slate-400" />
        </button>
      </div>
      {!isComp && (
        <div className="grid grid-cols-3 gap-3 mt-3">
          <div>
            <p className="text-[10px] text-slate-400 uppercase">Target</p>
            <p className="text-sm font-bold text-slate-700">{formatCurrency(target)}</p>
          </div>
          <div>
            <p className="text-[10px] text-slate-400 uppercase">Sales</p>
            <p className="text-sm font-bold" style={{ color }}>QAR {formatCurrency(loc.current_sales ?? 0)}</p>
          </div>
          <div>
            <p className="text-[10px] text-slate-400 uppercase">Status</p>
            <p className="text-sm font-bold" style={{ color }}>{target > 0 ? `${(ratio * 100).toFixed(0)}%` : '—'}</p>
          </div>
        </div>
      )}
      {loc.description && <p className="text-[11px] text-slate-500 mt-2">{loc.description}</p>}
      {loc.entered_name && <p className="text-[10px] text-slate-400 mt-2">Last update by {loc.entered_name}</p>}
      {!isComp && scoutEnabled && onScout && (
        <button
          onClick={() => onScout(loc)}
          disabled={scouting}
          className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-[11px] font-bold transition-all disabled:opacity-50 disabled:cursor-wait"
        >
          {scouting ? (
            <><Loader2 size={13} className="animate-spin" /> Scanning Area…</>
          ) : (
            <><Radar size={13} /> 🔍 Scout Competitors</>
          )}
        </button>
      )}
    </motion.div>
  );
}
